/**
 * Issue #322 — the per-kind triage body for a `ResolveContributorShape` contribution: the
 * inline contributor-shape editor the treasurer uses to fix a person-vs-organization
 * type/kind contradiction (the Ledyard/Huston Alabama-rejection bug) right inside the
 * contribution's expanded details — never leaving for a separate contact page.
 *
 * It edits the SHARED donor `FinanceContact` (every gift from that donor is corrected at
 * once) AND the contribution's type TOGETHER, atomically, via
 * `POST /api/finance/contributions/{id}/resolve-contributor-shape`, so the two always end up
 * in agreement — the exact thing the old ledger reclassify (which edited only the type and
 * silently no-op'd the contact) failed to do.
 *
 * It PRE-FILLS the donor's current name string as the starting value for a split, but NEVER
 * auto-splits it (ADR-004) — the treasurer types the first and last name. A 409 from a stale
 * contribution/contact token surfaces the refresh prompt; a still-disagreeing shape 400s.
 *
 * ADR-057 (#344) — the person/organization gift members (Individual/Pac/Party/Corporate) were
 * retired, so a gift no longer carries a person-vs-org identity that can contradict its
 * contact. The resolver sets the contact's ContactKind (person vs organization) and sends the
 * identity-less `Cash` receipt nature, which is Neutral on the person-vs-org axis and therefore
 * agrees with whichever shape the treasurer picks. The organization SUBTYPE (PAC / party /
 * corporation) is set separately on the contact via the contact-detail OrganizationType picker;
 * this resolver's request contract carries only the person-vs-org shape.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ContactKind,
  ReceiptNature,
  type TriageItem,
} from '@signalvote/shared';
import { Button } from '@/components/ui/button';
import {
  useResolveContributorShape,
  StagedRowUpdateError,
} from '@/hooks/queries';
import { financeFormInputClass } from '@/components/finance/shared/financeControlClasses';
import type { RegisterConfirmFn } from './PendingContributionBody';

interface ContributorShapeBodyProps {
  item: Extract<TriageItem, { kind: 'Contribution' }>;
  /** The shared donor contact's current version — a stale token 409s (mirrors MissingInfoBody). */
  contactConcurrencyVersion: number;
  onClassified: () => void;
  /** Modal-only: register Enter → Save. Omitted/noop on the inline ledger surface. */
  onRegisterConfirm?: RegisterConfirmFn;
  /**
   * Which shape to open on. Defaults to Person (the reported bug is a person mis-stored as an
   * org). When the donor-info panel opens this to escape its dead-end, it passes the OPPOSITE of
   * the donor's current kind so the toggle starts on the shape the treasurer is switching to.
   */
  initialShape?: ContactKind;
}

export function ContributorShapeBody({
  item,
  contactConcurrencyVersion,
  onClassified,
  onRegisterConfirm,
  initialShape = ContactKind.Individual,
}: ContributorShapeBodyProps) {
  const contribution = item.contribution;
  const currentName = contribution.contactName ?? '';

  // The blast radius of the fix: editing the SHARED donor touches every gift from that donor, so
  // the note names the count, or a qualitative phrase when it is zero.
  const giftCount = contribution.contactContributionCount;
  const giftsClause =
    giftCount > 1
      ? `all ${giftCount} of their gifts`
      : giftCount === 1
        ? 'their one gift'
        : 'every gift from them';

  // Defaults to Person (the reported bug is a real individual mis-stored as an org); the
  // donor-info escape hatch passes the opposite of the donor's current kind.
  const [shape, setShape] = useState<ContactKind>(initialShape);
  // ADR-004 — pre-fill the current name as the STARTING value for the split; the treasurer
  // moves the surname into Last name. Never auto-split. A donor already stored as a person
  // starts from their own parts, and their middle name and suffix ride the save untouched.
  const isPersonDonor = contribution.contactKind === ContactKind.Individual;
  const [firstName, setFirstName] = useState(
    isPersonDonor ? (contribution.contactFirstName ?? '') : currentName,
  );
  const [lastName, setLastName] = useState(
    isPersonDonor ? (contribution.contactLastName ?? '') : '',
  );
  const [orgName, setOrgName] = useState(currentName);
  const [error, setError] = useState<string | null>(null);

  const { mutateAsync: resolveShapeAsync, isPending } = useResolveContributorShape();

  // ADR-057 (#344) — the resolver sends the identity-less `Cash` receipt nature. It is Neutral
  // on the person-vs-org axis, so it agrees with whichever shape the treasurer picks (person or
  // organization); the person-vs-org fix now lives entirely on the contact's ContactKind.
  const targetType = useMemo<ReceiptNature>(() => ReceiptNature.Cash, []);

  const canSave =
    shape === ContactKind.Individual
      ? firstName.trim().length > 0 && lastName.trim().length > 0
      : orgName.trim().length > 0;

  const save = useCallback(async () => {
    if (!canSave || isPending) return;
    setError(null);
    try {
      await resolveShapeAsync({
        contributionId: contribution.id,
        concurrencyVersion: contribution.concurrencyVersion,
        contactConcurrencyVersion,
        contactKind: shape,
        contributionType: targetType,
        firstName: shape === ContactKind.Individual ? firstName.trim() : null,
        middleName: shape === ContactKind.Individual ? contribution.contactMiddleName : null,
        lastName: shape === ContactKind.Individual ? lastName.trim() : null,
        nameSuffix: shape === ContactKind.Individual ? contribution.contactNameSuffix : null,
        organizationName: shape === ContactKind.Organization ? orgName.trim() : null,
      });
      onClassified();
    } catch (err) {
      setError(
        err instanceof StagedRowUpdateError
          ? err.message
          : 'Could not save. Please try again.',
      );
    }
  }, [
    canSave,
    isPending,
    resolveShapeAsync,
    contribution.id,
    contribution.concurrencyVersion,
    contribution.contactMiddleName,
    contribution.contactNameSuffix,
    contactConcurrencyVersion,
    shape,
    targetType,
    firstName,
    lastName,
    orgName,
    onClassified,
  ]);

  // useRef stabilizes the handler identity so the registration effect doesn't
  // re-fire on every render. `save` depends on the modal's `onClassified`
  // (recreated each render), so registering `{ fn: () => save() }` with `save`
  // in the deps looped: effect → setBodyConfirm(new object) → modal re-render →
  // new onClassified → new save → effect… ("Maximum update depth exceeded").
  // Mirrors MissingInfoBody / PendingContributionBody — register a stable
  // `{ fn: () => ref.current() }` and key the effect only on `canSave`.
  const saveRef = useRef(save);
  useEffect(() => {
    saveRef.current = save;
  }, [save]);

  // Modal Enter → Save. Registers only while the form is valid; the stable
  // ref indirection keeps the latest `save` reachable without re-registering.
  useEffect(() => {
    onRegisterConfirm?.(canSave ? { fn: () => void saveRef.current() } : null);
    return () => onRegisterConfirm?.(null);
  }, [onRegisterConfirm, canSave]);

  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-muted-foreground">
        This gift&rsquo;s type and the donor&rsquo;s record disagree on whether{' '}
        <span className="font-medium text-foreground">{currentName || 'this donor'}</span> is a
        person or an organization. Set the donor to match so Alabama accepts the file. This fixes
        the donor everywhere &mdash; {giftsClause}.
      </p>

      <div className="flex gap-2" role="radiogroup" aria-label="Donor type">
        <Button
          type="button"
          variant={shape === ContactKind.Individual ? 'default' : 'outline'}
          aria-pressed={shape === ContactKind.Individual}
          onClick={() => setShape(ContactKind.Individual)}
        >
          Person
        </Button>
        <Button
          type="button"
          variant={shape === ContactKind.Organization ? 'default' : 'outline'}
          aria-pressed={shape === ContactKind.Organization}
          onClick={() => setShape(ContactKind.Organization)}
        >
          Organization
        </Button>
      </div>

      {shape === ContactKind.Individual ? (
        <div className="flex flex-col gap-2">
          <label className="text-xs font-medium">
            First name
            <input
              className={financeFormInputClass}
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              aria-label="First name"
              autoFocus
            />
          </label>
          <label className="text-xs font-medium">
            Last name
            <input
              className={financeFormInputClass}
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              aria-label="Last name"
            />
          </label>
          <p className="text-xs text-muted-foreground">
            We start with the name we have &mdash; split it into first and last yourself. We never
            guess the split.
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          <label className="text-xs font-medium">
            Organization name
            <input
              className={financeFormInputClass}
              value={orgName}
              onChange={(e) => setOrgName(e.target.value)}
              aria-label="Organization name"
              autoFocus
            />
          </label>
          <p className="text-xs text-muted-foreground">
            Set the organization&rsquo;s type (PAC, party, corporation&hellip;) on the
            donor&rsquo;s contact record.
          </p>
        </div>
      )}

      {error && <p className="text-sm text-destructive">{error}</p>}

      <div className="flex justify-end">
        <Button type="button" onClick={() => void save()} disabled={!canSave || isPending}>
          {isPending ? 'Saving…' : 'Save donor'}
        </Button>
      </div>
    </div>
  );
}
